import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Kiosk from "@/pages/Kiosk";
import Dangoods from "@/pages/Dangoods";
import Tradepartner from "@/pages/Tradepartner";
import Stockgroup from "@/pages/Stockgroup";
import TilbudsAvisPublic from "@/pages/TilbudsAvisPublic";
import TilbudsAvisHome from "@/pages/TilbudsAvisHome";
import JatakPublic from "@/pages/JatakPublic";
import { Loader2 } from "lucide-react";

// Når appen deployes på tilbudsdomænet (lagersalgtilbud.dk) bliver forsiden den
// offentlige oversigt over tilbudsaviser i stedet for dashboardet. Det afgøres
// automatisk ud fra domænet, så det følger med via GitHub uden at skulle sætte
// nogen secret. REACT_APP_SITE_MODE=tilbud kan bruges som manuel override (fx preview).
const SITE_MODE = process.env.REACT_APP_SITE_MODE;
const HOSTNAME = typeof window !== "undefined" ? window.location.hostname : "";
const IS_TILBUD = SITE_MODE === "tilbud" || /lagersalgtilbud/i.test(HOSTNAME);

// Fanens titel + ikon: tilbudsdomænet får sit eget, stats-dashboardet beholder sit.
if (typeof document !== "undefined" && IS_TILBUD) {
  document.title = "Lagersalgtilbud - Se ugens avis";
  let link = document.querySelector("link[rel='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.type = "image/svg+xml";
  link.href = "/favicon-tilbud.svg";
}

function ProtectedRoute({ children }) {
  const { user, checking } = useAuth();
  if (checking || user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F7F6]">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Non-admin roles are restricted to their own area.
function homePath(role) {
  if (role === "dangoods") return "/dangoods";
  if (role === "tradepartner") return "/tradepartner";
  if (role === "stockgroup") return "/stockgroup";
  // På tilbudsdomænet er "/" den offentlige forside, så admin sendes til /dashboard.
  return IS_TILBUD ? "/dashboard" : "/";
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to={homePath(user.role)} replace />;
  return children;
}

function LoginRoute() {
  const { user, checking } = useAuth();
  if (checking) return null;
  if (user) return <Navigate to={homePath(user.role)} replace />;
  return <Login />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/avis/:slug" element={<TilbudsAvisPublic />} />
            <Route path="/ja-tak" element={<JatakPublic />} />
            <Route
              path="/"
              element={
                IS_TILBUD ? (
                  <TilbudsAvisHome />
                ) : (
                  <ProtectedRoute>
                    <AdminOnly>
                      <Dashboard />
                    </AdminOnly>
                  </ProtectedRoute>
                )
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <AdminOnly>
                    <Dashboard />
                  </AdminOnly>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dangoods"
              element={
                <ProtectedRoute>
                  <Dangoods />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tradepartner"
              element={
                <ProtectedRoute>
                  <Tradepartner />
                </ProtectedRoute>
              }
            />
            <Route
              path="/stockgroup"
              element={
                <ProtectedRoute>
                  <Stockgroup />
                </ProtectedRoute>
              }
            />
            <Route
              path="/kiosk"
              element={
                <ProtectedRoute>
                  <AdminOnly>
                    <Kiosk />
                  </AdminOnly>
                </ProtectedRoute>
              }
            />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
